import { AppRoutes } from "@/constants/routes";
import { routesWithPermissionCodes } from "@/constants/routes-permissions";
import { RolesContext } from "@/context/RolesContext";
import { Permiso } from "@/pages/role-asignation/schema/role-assignation.schema";
import {
  getListadoRoles,
  getUserPermissions,
} from "@/services/users-bo.service";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

interface Props {
  children: ReactNode;
}

export const RolesProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingPermissions, setloadingPermissions] = useState<boolean>(false);
  const [userPermissions, setUserPermissions] = useState<Permiso[] | null>([]);

  const fetchAllRoles = async () => {
    return await getListadoRoles({});
  };

  const fetchPermissions = async (showLoading: boolean = true) => {
    try {
      if (showLoading) setloadingPermissions(true);
      if (localStorage.getItem("msalUser")) {
const response = await getUserPermissions();

        setUserPermissions(response);
      }

      setloadingPermissions(false);
    } catch (error) {
      setloadingPermissions(false);
      throw error;
    }
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) fetchPermissions(true);
  }, [isActive]);

  const [permissionsChecked, setPermissionsChecked] = useState(false);

  const checkPermissions = () => {
    if (loadingPermissions || !userPermissions || userPermissions.length === 0)
      return;

    const currentPath = location.pathname;

    // 🔹 Buscar TODAS las rutas que coinciden
    const matchedRoutes = routesWithPermissionCodes.filter((r) => {
      const routes = Array.isArray(r.route) ? r.route : [r.route];
      return routes?.some((path) =>
        matchPath({ path, end: true }, currentPath),
      );
    });

    if (matchedRoutes.length === 0) return;

    // 🔹 Priorizar la ruta que requiere escritura si hay múltiples matches
    const matchedRoute =
      matchedRoutes.find((r) => r.requireWriteAccess) || matchedRoutes[0];

    const requiredPermissions = matchedRoute.permissions;

    const hasReadAccess = requiredPermissions.some((permCode) =>
      userPermissions.some(
        (up) => up.codigo === permCode && up.tipoPermiso === "R",
      ),
    );

    const hasWriteAccess = requiredPermissions.some((permCode) =>
      userPermissions.some(
        (up) => up.codigo === permCode && up.tipoPermiso === "W",
      ),
    );
    
    // 🔹 Si requiere escritura, TAMBIÉN necesita lectura
    if (matchedRoute.requireWriteAccess) {
      if (!hasReadAccess || !hasWriteAccess) {
        navigate(AppRoutes.NO_PERMISSION);
        return;
      }
    }
    // 🔹 Si solo requiere lectura
    else if (!hasReadAccess) {
      navigate(AppRoutes.NO_PERMISSION);
      return;
    }

    setPermissionsChecked(true);
  };

  useEffect(() => {
    if (
      isActive &&
      !permissionsChecked &&
      !loadingPermissions &&
      Number(userPermissions?.length) > 0
    ) {
      checkPermissions();
    }
  }, [userPermissions, isActive, location.pathname, loadingPermissions]);

  const contextValue = useMemo(
    () => ({
      userPermissions,
      loadingPermissions,
      fetchAllRoles,
      permissionList: userPermissions?.map((el) => el.codigo) || [],
      fetchPermissions,
    }),
    [userPermissions, loadingPermissions, fetchAllRoles, fetchPermissions],
  );

  return (
    <RolesContext.Provider value={contextValue}>
      {children}
    </RolesContext.Provider>
  );
};
